import { RouterProvider } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { Toaster } from "@/components/ui/toaster";
import Sonner from "@/components/ui/Sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import LoadContextProvider from "@/contexts/LoadContextProvider";
import { AuthProvider, initializeApiClient, initializeFirebaseAuth } from "@/auth";
import { type AppConfig } from "@/config";
import ErrorBoundary from "@/ErrorBoundary";
import TigimRoutes from "@/routes";
import "react-day-picker/style.css";
import React from "react";

const ReactQueryDevtoolsProduction = React.lazy(() =>
  import("@tanstack/react-query-devtools/build/modern/production.js").then(d => ({
    default: d.ReactQueryDevtools
  }))
);

posthog.init(import.meta.env.TIGIM_PUBLIC_POSTHOG_KEY, {
  api_host: import.meta.env.TIGIM_PUBLIC_POSTHOG_HOST,
  defaults: "2025-05-24",
  capture_exceptions: {
    capture_unhandled_errors: true,
    capture_unhandled_rejections: true,
    capture_console_errors: true
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      gcTime: 1000 * 60 * 60 * 24 // 24 hours
    }
  }
});

interface AppProps {
  config: AppConfig;
}

const App = ({ config }: AppProps) => {
  initializeFirebaseAuth(config);
  initializeApiClient(config.api.baseUrl);

  /**
   * Show the devtools in production mode with window.toggleDevtools() function
   */
  const [showDevtools, setShowDevtools] = React.useState(false);

  React.useEffect(() => {
    // @ts-expect-error - window.toggleDevtools is not typed
    window.toggleDevtools = () => setShowDevtools(old => !old);
  }, []);

  return (
    <ErrorBoundary componentName="App">
      <PostHogProvider client={posthog}>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} />
          {showDevtools && (
            <React.Suspense fallback={null}>
              <ReactQueryDevtoolsProduction />
            </React.Suspense>
          )}
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <LoadContextProvider>
              <AuthProvider>
                <RouterProvider router={TigimRoutes} />
              </AuthProvider>
            </LoadContextProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </PostHogProvider>
    </ErrorBoundary>
  );
};

export default App;
